import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, Search, X } from '@/design-system/icons'
import { SidebarSimple, Star } from '@phosphor-icons/react'
import { useAuth } from '@/stores/auth'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn, CountBadge, Icon, Kbd } from '@/design-system'
import { useT } from '@/i18n'
import { useNavBadges } from '@/navigation/badges'
import { hasNewPage, isNewPage } from '@/navigation/newBadge'
import type { NavGroup, NavNode, NavPage } from '@/navigation/types'
import { isPageActive, searchNav, useActiveNav, useVisibleNav } from '@/navigation/useNavigation'
import { useUi } from '@/stores/ui'
import { preloadPath } from '@/app/pageRegistry'
import { pageIcon } from '@/navigation/pageIcons'
import { AliensMark, AliensWordmark } from '@/design-system'
import { useTabLink } from './workspace/tabMeta'
import { MobileNav } from './MobileNav'

export function Sidebar({ variant }: { variant: 'desktop' | 'mobile' }) {
  // the phone gets its own menu: one level at a time, rows for a thumb (layout/MobileNav.tsx)
  if (variant === 'mobile') return <MobileNav />
  return <DesktopSidebar variant={variant} />
}

function DesktopSidebar({ variant }: { variant: 'desktop' | 'mobile' }) {
  const { t, l } = useT()
  const tree = useVisibleNav()
  const active = useActiveNav()
  const collapsed = useUi((s) => s.sidebarCollapsed) && variant === 'desktop'
  const toggleSidebar = useUi((s) => s.toggleSidebar)
  const expandNavGroups = useUi((s) => s.expandNavGroups)
  const openNavSection = useUi((s) => s.openNavSection)
  const favorites = useUi((s) => s.favorites)
  const topIds = useMemo(() => tree.map((s) => s.id), [tree])
  // the starred pages, in the order they were starred; a page this user can no longer see drops out
  const favPages = useMemo(() => {
    const byId = new Map<string, NavPage>()
    const walk = (n: NavNode) => (n.kind === 'page' ? byId.set(n.id, n) : n.children.forEach(walk))
    tree.forEach(walk)
    return favorites.map((id) => byId.get(id)).filter((p): p is NavPage => Boolean(p))
  }, [tree, favorites])
  const setMobileNav = useUi((s) => s.setMobileNav)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const tabLink = useTabLink()

  // Reveal the active page: its section opens and the other sections close (owner, 2026-10-20: 300+ pages all open
  // at once was too much), then its sub-groups open. Once per navigation.
  useEffect(() => {
    if (!active) return
    const [top, ...rest] = active.trail.map((g) => g.id)
    if (top) openNavSection(top, topIds)
    if (rest.length) expandNavGroups(rest)
  }, [active?.page.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault()
        if (collapsed) toggleSidebar()
        setTimeout(() => searchRef.current?.focus(), 50)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [collapsed, toggleSidebar])

  const results = useMemo(() => searchNav(tree, query), [tree, query])
  const closeMobile = () => variant === 'mobile' && setMobileNav(false)

  return (
    <nav aria-label="Main" className={cn('sidebar-card relative flex h-full flex-col overflow-hidden bg-sidebar pt-[env(safe-area-inset-top)] text-sidebar-fg', variant === 'desktop' && 'rounded-[24px] border border-sidebar-border shadow-sm')}>
      {/* the head: the brand and, beside it, the open / close button (Flowly Groups' sb-top) */}
      <div className={cn('flex shrink-0 items-center gap-2', collapsed ? 'flex-col justify-center px-2 pb-2 pt-4' : 'justify-between px-5 pb-3 pt-5')}>
        <Link to="/" {...tabLink('/', closeMobile)} className="brand-block group flex min-w-0 items-center gap-3 text-sidebar-fg-strong">
          {/* the tile when closed, the Aliens logo alone when open (owner, 2026-11-01: «لما سايد بار مقفول ايقونة … لما يفتح لوجو aliens الي يظهر بس») */}
          {collapsed ? (
            <span className="brand-tile relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[12px]">
              <AliensMark tone="white" className="relative z-[1] h-[19px] w-auto drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]" title="Aliens" />
            </span>
          ) : (
            <span className="min-w-0 leading-none" dir="ltr">
              <span className="flex items-center gap-2">
                <AliensWordmark className="h-[19px] w-auto" />
                <span className="rounded-[5px] bg-brand px-1.5 py-[3px] text-[9.5px] font-extrabold leading-none tracking-[0.16em] text-white">OS</span>
              </span>
              <span className="mt-1.5 block truncate text-[11px] font-medium text-sidebar-fg" dir="auto">{t('app.tagline')}</span>
            </span>
          )}
        </Link>
        {variant === 'mobile' ? (
          <button onClick={closeMobile} className="rounded-lg p-1.5 hover:bg-sidebar-hover" aria-label={t('common.close')}>
            <X size={18} aria-hidden />
          </button>
        ) : (
          <button onClick={toggleSidebar} className="flex size-9 shrink-0 items-center justify-center rounded-xl text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-fg-strong" aria-label={t('shell.toggleSidebar')} title={t('shell.toggleSidebar')}>
            <SidebarSimple size={20} weight="duotone" className="rtl:-scale-x-100" aria-hidden />
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="shrink-0 px-4 pb-2">
          <div className="relative flex items-center">
            <Search size={14} className="pointer-events-none absolute start-2.5 text-sidebar-fg" aria-hidden />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
              placeholder={t('shell.searchNav')}
              aria-label={t('shell.searchNav')}
              className="h-9 w-full rounded-[10px] border border-transparent bg-sidebar-hover pe-8 ps-8 text-xs text-sidebar-fg-strong transition-colors placeholder:text-sidebar-fg/70 hover:border-sidebar-border focus:border-brand/50 focus:bg-surface focus:shadow-[0_0_0_3px_rgba(174,26,28,0.08)] focus:outline-none"
            />
            <span className="absolute end-2">{query ? <button onClick={() => setQuery('')} className="text-sidebar-fg" aria-label={t('common.clear')}><X size={13} aria-hidden /></button> : <Kbd>/</Kbd>}</span>
          </div>
        </div>
      )}

      <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3', collapsed ? 'px-3 pt-1' : 'px-3')}>
        {query && !collapsed ? (
          <ul className="space-y-0.5">
            {results.length === 0 && <li className="px-3 py-6 text-center text-xs">{t('shell.noNavResults')}</li>}
            {results.map((r) => (
              <li key={r.page.id}>
                <Link
                  to={r.page.path}
                  {...tabLink(r.page.path, () => (setQuery(''), closeMobile()))}
                  className="block rounded-md px-3 py-2 hover:bg-sidebar-hover"
                >
                  <span className="block text-sm text-sidebar-fg-strong">{l(r.page.label)}</span>
                  <span className="block truncate text-2xs">{r.trail.map((g) => l(g.label)).join(' › ')}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="space-y-0.5">
            {/* المفضلة: the pages this user starred, always on top */}
            {!collapsed && favPages.length > 0 && (
              <li className="mb-2">
                <p className="mt-2 flex h-7 items-center gap-1.5 px-2 text-[11.5px] font-bold tracking-[0.02em] text-sidebar-fg"><Star size={12} weight="fill" className="text-amber-500" aria-hidden />المفضلة</p>
                <ul className="space-y-0.5">{favPages.map((p) => <PageItem key={`fav-${p.id}`} page={p} onNavigate={closeMobile} />)}</ul>
              </li>
            )}
            {tree.map((section) => (
              <SectionItem key={section.id} section={section} collapsed={collapsed} activeTrail={active?.trail.map((g) => g.id) ?? []} onNavigate={closeMobile} topIds={topIds} />
            ))}
          </ul>
        )}
      </div>

      <SidebarUser collapsed={collapsed} />
    </nav>
  )
}

/** Who is signed in, at the sidebar's foot (Flowly Groups' sb-user): the initials, the name, the branch's account. */
function SidebarUser({ collapsed }: { collapsed: boolean }) {
  const user = useAuth((s) => s.user)
  if (!user) return null
  const name = user.displayName || user.username
  const initials = name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  return (
    <div className="shrink-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className={cn('flex items-center gap-2.5 rounded-2xl border border-sidebar-border bg-sidebar-hover p-2', collapsed && 'justify-center')} title={name}>
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: 'linear-gradient(140deg,#e0383b,#ae1a1c)' }}>{initials}</span>
        {!collapsed && (
          <span className="min-w-0 flex-1 leading-tight">
            <b className="block truncate text-[13px] text-sidebar-fg-strong">{name}</b>
            <small className="block truncate text-[11px] text-sidebar-fg" dir="ltr">{user.username}</small>
          </span>
        )}
      </div>
    </div>
  )
}

/** Height animation without measuring: children mount on first open and stay mounted for smooth collapse. */
function Collapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) setMounted(true)
  }, [open])
  return (
    <div className="collapse-grid" data-open={open}>
      <div>{mounted ? children : null}</div>
    </div>
  )
}

/** The red "NEW" of a feature's first week (navigation/newBadge.ts). */
/** A new page's first week: a small red dot, not the word — the red labels everywhere scattered the eye (owner, 2026-10-20). */
function NewBadge() {
  return <span className="size-2 shrink-0 rounded-full bg-danger" title="جديد" aria-label="جديد" />
}

function countBadges(node: NavNode, badges: Record<string, number | undefined>): number {
  if (node.kind === 'page') return node.badge ? (badges[node.badge] ?? 0) : 0
  // A shared badge (e.g. notifications listed twice) should not double count.
  const keys = new Set<string>()
  const walk = (n: NavNode) => (n.kind === 'page' ? n.badge && keys.add(n.badge) : n.children.forEach(walk))
  walk(node)
  return [...keys].reduce((s, k) => s + (badges[k] ?? 0), 0)
}

const SectionItem = memo(function SectionItem({ section, collapsed, activeTrail, onNavigate, topIds = [] }: { section: NavGroup; collapsed: boolean; activeTrail: string[]; onNavigate: () => void; topIds?: string[] }) {
  const openNavSection = useUi((s) => s.openNavSection)
  const { l } = useT()
  const expanded = useUi((s) => s.expandedNav.includes(section.id))
  const toggle = useUi((s) => s.toggleNavGroup)
  const badges = useNavBadges()
  const isActive = activeTrail[0] === section.id
  const total = countBadges(section, badges)

  if (collapsed) {
    return (
      <li>
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              className={cn('nav-row group relative flex w-full items-center justify-center rounded-[14px] p-3 transition-colors hover:bg-sidebar-hover hover:text-sidebar-fg-strong data-[state=open]:bg-sidebar-hover', isActive && 'bg-sidebar-active text-sidebar-active-fg shadow-[inset_0_0_0_1px_rgba(174,26,28,0.14)]')}
              aria-label={l(section.label)}
              title={l(section.label)}
            >
              {section.icon && <span className="nav-icon"><Icon icon={section.icon} size={20} weight={isActive ? 'fill' : 'duotone'} /></span>}
              {total > 0 && <span className="absolute end-1.5 top-1.5 size-2 rounded-full bg-accent" />}
              {hasNewPage(section) && <span className="absolute bottom-1 end-1 size-2 rounded-full bg-danger" />}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content side={document.documentElement.dir === "rtl" ? "left" : "right"} align="start" sideOffset={14} collisionPadding={12} className="z-50 max-h-[80vh] w-72 overflow-y-auto rounded-2xl border border-sidebar-border bg-surface-raised p-2 text-sidebar-fg shadow-xl data-[state=open]:animate-scale-in">
              <p className="flex items-center gap-2 border-b border-sidebar-border px-2 pb-2.5 pt-1.5 text-[13.5px] font-bold text-sidebar-fg-strong">{section.icon && <Icon icon={section.icon} size={18} weight="duotone" />}{l(section.label)}</p>
              <NodeList nodes={section.children} depth={1} onNavigate={onNavigate} activeTrail={activeTrail} forceExpanded />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </li>
    )
  }

  return (
    <li>
      <button
        // one section open at a time: opening this one closes the others
        onClick={() => (expanded ? toggle(section.id) : openNavSection(section.id, topIds))}
        aria-expanded={expanded}
        // a section is a real row with its icon (owner, 2026-10-20: «فين SVG ICON والشكل الشيك»): the open one soft red
        className={cn('nav-row group relative flex min-h-11 w-full items-center gap-3 rounded-[12px] px-3 py-1.5 transition-colors duration-200', isActive ? 'bg-sidebar-active shadow-[inset_0_0_0_1px_rgba(174,26,28,0.14)]' : expanded ? 'bg-sidebar-hover' : 'hover:bg-sidebar-hover')}
      >
        {section.icon && (
          <span className="nav-icon flex shrink-0 items-center justify-center">
            <Icon icon={section.icon} size={21} weight={isActive ? 'fill' : 'duotone'} />
          </span>
        )}
        <span title={l(section.label)} className={cn('line-clamp-2 min-w-0 flex-1 text-start text-[14.5px] leading-snug', isActive ? 'font-semibold text-sidebar-active-fg' : 'font-medium text-sidebar-fg-strong')}>{l(section.label)}</span>
        {hasNewPage(section) && <NewBadge />}
        {!expanded && <CountBadge value={total} />}
        <ChevronDown size={14} className={cn('nav-chevron shrink-0 opacity-60', !expanded && 'ltr:-rotate-90 rtl:rotate-90')} aria-hidden />
      </button>
      <Collapse open={expanded}><NodeList nodes={section.children} depth={1} onNavigate={onNavigate} activeTrail={activeTrail} /></Collapse>
    </li>
  )
})

function NodeList({ nodes, depth, onNavigate, activeTrail, forceExpanded }: { nodes: NavNode[]; depth: number; onNavigate: () => void; activeTrail: string[]; forceExpanded?: boolean }) {
  return (
    // the pages of an open section, indented under its icon with a thin guide line
    <ul className={cn('space-y-0.5', 'nav-stagger', depth === 1 && !forceExpanded && 'mb-2 ms-[26px] mt-1 border-s border-sidebar-border ps-2')}>
      {nodes.map((n) => (n.kind === 'page' ? <PageItem key={n.id} page={n} onNavigate={onNavigate} /> : <SubsectionItem key={n.id} group={n} depth={depth} onNavigate={onNavigate} activeTrail={activeTrail} forceExpanded={forceExpanded} />))}
    </ul>
  )
}

function SubsectionItem({ group, depth, onNavigate, activeTrail, forceExpanded }: { group: NavGroup; depth: number; onNavigate: () => void; activeTrail: string[]; forceExpanded?: boolean }) {
  const { l } = useT()
  const expandedState = useUi((s) => s.expandedNav.includes(group.id))
  const toggle = useUi((s) => s.toggleNavGroup)
  const badges = useNavBadges()
  const expanded = expandedState
  const isActive = activeTrail.includes(group.id)
  const total = countBadges(group, badges)
  return (
    <li>
      <button
        onClick={() => toggle(group.id)}
        aria-expanded={expanded}
        className="flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-[13px] transition-colors hover:bg-sidebar-hover"
      >
        <span title={l(group.label)} className="line-clamp-2 min-w-0 flex-1 text-start font-semibold leading-snug text-sidebar-fg-strong">{l(group.label)}</span>
        {hasNewPage(group) && <NewBadge />}
        {!expanded && <CountBadge value={total} />}
        <ChevronDown size={13} className={cn('nav-chevron shrink-0 opacity-50', !expanded && 'ltr:-rotate-90 rtl:rotate-90')} aria-hidden />
      </button>
      <Collapse open={expanded || Boolean(forceExpanded && isActive)}>
        <ul className="nav-stagger mb-1 ms-2 space-y-0.5 border-s border-sidebar-border ps-2">
          {group.children.map((n) => (n.kind === 'page' ? <PageItem key={n.id} page={n} onNavigate={onNavigate} /> : <SubsectionItem key={n.id} group={n} depth={depth + 1} onNavigate={onNavigate} activeTrail={activeTrail} />))}
        </ul>
      </Collapse>
    </li>
  )
}

function PageItem({ page, onNavigate }: { page: NavPage; onNavigate: () => void }) {
  const { l } = useT()
  const { pathname, search } = useLocation()
  const badges = useNavBadges()
  const active = isPageActive(page, pathname, search)
  const PIcon = pageIcon(page.id)
  const fav = useUi((s) => s.favorites.includes(page.id))
  const toggleFavorite = useUi((s) => s.toggleFavorite)
  // click: open or focus the page's tab · Ctrl/⌘-click or middle-click: open it in the background
  const tabLink = useTabLink()
  return (
    <li>
      <Link
        to={page.path}
        {...tabLink(page.path, onNavigate)}
        onMouseEnter={() => preloadPath(page.path)}
        onFocus={() => preloadPath(page.path)}
        aria-current={active ? 'page' : undefined}
        className={cn(
          // a long name (توزيع شحنات على مناديب) takes a second line instead of being cut (owner, 2026-10-23)
          'nav-row group relative flex min-h-10 items-center gap-3 rounded-[11px] px-3 py-1.5 transition-colors duration-200',
          active ? 'nav-active bg-sidebar-active shadow-[inset_0_0_0_1px_rgba(174,26,28,0.14)]' : 'hover:bg-sidebar-hover',
        )}
      >
        {PIcon && <span className="nav-icon nav-mono flex shrink-0"><PIcon size={20} weight={active ? 'fill' : 'duotone'} aria-hidden /></span>}
        <span title={l(page.label)} className={cn('line-clamp-2 min-w-0 flex-1 text-sm leading-snug', active ? 'font-semibold text-sidebar-active-fg' : 'font-medium text-sidebar-fg')}>{l(page.label)}</span>
        {page.status === 'gap' && <span className="size-1.5 shrink-0 rounded-full bg-sidebar-fg/40" title="Pending backend" />}
        {isNewPage(page) && <NewBadge />}
        {page.badge && <CountBadge value={badges[page.badge]} />}
        {/* the star: shows on hover, stays when starred (owner, 2026-10-20: «المفضلة»); a phone has no hover, so there
            it is always shown, faint until starred (owner, 2026-10-23: «ايقونه المفضله مش ظاهره ف ديزاين موبايل») */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(page.id) }}
          className={cn('-me-1 flex size-8 shrink-0 items-center justify-center rounded-md transition-opacity hover:bg-surface sm:size-6', fav ? 'text-amber-500' : 'text-sidebar-icon opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-50')}
          aria-label={fav ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
          title={fav ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
        >
          <Star size={14} weight={fav ? 'fill' : 'regular'} aria-hidden />
        </button>
      </Link>
    </li>
  )
}
